import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  Package,
  DollarSign,
  Filter,
  Download,
  TrendingUp,
  TrendingDown,
  Activity,
  PieChart,
  LineChart,
  Receipt,
  Target,
  FileText,
  BarChart,
  BarChart3,
  RefreshCw,
  X
} from 'lucide-react';

const FinancialReport = () => {
  console.log('🎯 FinancialReport component rendering...');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Report data state
  const [financialData, setFinancialData] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    grossProfit: 0,
    profitMargin: 0,
    cashFlow: {
      operating: 0,
      investing: 0,
      financing: 0,
      net: 0
    },
    revenueBySource: [],
    expenseBreakdown: [],
    monthlyTrend: [],
    financialRatios: {
      currentRatio: 0,
      quickRatio: 0,
      debtToEquity: 0,
      returnOnInvestment: 0
    }
  });

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Modal state
  const [showProfitLossModal, setShowProfitLossModal] = useState(false);
  const [showIncomeStatementModal, setShowIncomeStatementModal] = useState(false);
  


  // Helper function to format currency
  const formatCurrency = (amount) => {
    return `Ghs ${parseFloat(amount || 0).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Helper function to format percentage
  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  // Fetch and aggregate actual expense data including supplier payments and purchases
  const fetchAndAggregateExpenseData = async () => {
    try {
      
      // Fetch expenses
      const expenseResponse = await apiService.getExpenses({
        per_page: 1000 // Get all expenses
      });
      
      const expenses = expenseResponse.data || [];
      console.log('📋 Total expenses from API:', expenses.length);
      
      // Fetch supplier payments
      let supplierPayments = [];
      try {
        console.log('🔄 Attempting to fetch supplier payments...');
        const supplierPaymentResponse = await apiService.getSupplierPayments({
          per_page: 1000 // Get all supplier payments
        });
        
        
        supplierPayments = supplierPaymentResponse.data || [];
        console.log('💳 Total supplier payments from API:', supplierPayments.length);
        
        if (supplierPayments.length > 0) {
          console.log('💳 Sample supplier payment:', supplierPayments[0]);
        }
      } catch (error) {
        console.error('❌ Failed to fetch supplier payments:', error);
        console.error('❌ Error details:', error.message);
        supplierPayments = [];
      }
      
      // Filter expenses by date range if needed
      let filteredExpenses = expenses;
      if (dateRange.startDate && dateRange.endDate) {
        console.log('  - Date range:', dateRange.startDate, 'to', dateRange.endDate);
        console.log('  - Total expenses before filtering:', expenses.length);
        
        filteredExpenses = expenses.filter(expense => {
          // Handle different date formats
          let expenseDate;
          if (expense.expense_date.includes('T')) {
            // ISO format: 2025-09-30T00:00:00.000Z
            expenseDate = expense.expense_date.split('T')[0];
          } else {
            // Simple format: 2025-09-30
            expenseDate = expense.expense_date;
          }
          
          const startDate = dateRange.startDate;
          const endDate = dateRange.endDate;
          
          const isInRange = expenseDate >= startDate && expenseDate <= endDate;
          
          return isInRange;
        });
        
        console.log('  - Total expenses after filtering:', filteredExpenses.length);
      }
      
      // Filter supplier payments by date range if needed
      let filteredSupplierPayments = supplierPayments;
      if (dateRange.startDate && dateRange.endDate) {
        console.log('  - Total supplier payments before filtering:', supplierPayments.length);
        
        filteredSupplierPayments = supplierPayments.filter(payment => {
          // Handle different date formats for supplier payments
          let paymentDate;
          if (payment.payment_date && payment.payment_date.includes('T')) {
            paymentDate = payment.payment_date.split('T')[0];
          } else if (payment.payment_date) {
            paymentDate = payment.payment_date;
          } else if (payment.created_at && payment.created_at.includes('T')) {
            paymentDate = payment.created_at.split('T')[0];
          } else if (payment.created_at) {
            paymentDate = payment.created_at;
          } else {
            return false; // Skip if no valid date
          }
          
          const startDate = dateRange.startDate;
          const endDate = dateRange.endDate;
          
          const isInRange = paymentDate >= startDate && paymentDate <= endDate;
          
          return isInRange;
        });
        
        console.log('  - Total supplier payments after filtering:', filteredSupplierPayments.length);
      }
      
      // Aggregate expenses by category
      const expenseBreakdown = {};
      let totalExpenses = 0;
      
      // Add regular expenses
      filteredExpenses.forEach(expense => {
        const category = expense.expense_account || 'Other';
        const amount = parseFloat(expense.amount) || 0;
        
        if (!expenseBreakdown[category]) {
          expenseBreakdown[category] = 0;
        }
        expenseBreakdown[category] += amount;
        totalExpenses += amount;
      });
      
      // Add supplier payments as expenses
      filteredSupplierPayments.forEach(payment => {
        const category = 'Supplier Payments';
        const amount = parseFloat(payment.amount) || 0;
        
        if (!expenseBreakdown[category]) {
          expenseBreakdown[category] = 0;
        }
        expenseBreakdown[category] += amount;
        totalExpenses += amount;
      });
      
      console.log('💰 Final expense breakdown:', expenseBreakdown);
      console.log('💵 Total expenses calculated (including supplier payments):', totalExpenses);
      console.log('💳 Supplier payments included:', filteredSupplierPayments.length);
      console.log('📋 Regular expenses included:', filteredExpenses.length);
      
      // Show detailed breakdown
      Object.entries(expenseBreakdown).forEach(([category, amount]) => {
        console.log(`  - ${category}: Ghs ${amount.toFixed(2)}`);
      });
      
      // Convert to array format with percentages
      const expenseBreakdownArray = Object.entries(expenseBreakdown).map(([category, amount]) => ({
        category,
        amount,
        percentage: totalExpenses > 0 ? ((amount / totalExpenses) * 100).toFixed(2) : 0
      }));
      
      console.log('Final expense breakdown:', expenseBreakdownArray);
      console.log('Total expenses calculated:', totalExpenses);
      
      return {
        totalExpenses,
        expenseBreakdown: expenseBreakdownArray
      };
    } catch (error) {
      console.error('Error fetching expense data:', error);
      return {
        totalExpenses: 0,
        expenseBreakdown: []
      };
    }
  };

  // NOTE: Mock data generation function removed - now using real API data only

  // Fetch financial report data
  const fetchFinancialReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getFinancialReport(dateRange.startDate, dateRange.endDate);
      if (response.success && response.data) {
        setFinancialData(response.data);
      } else {
        console.warn('⚠️ API returned no data, using empty state');
        setFinancialData({
          totalRevenue: 0,
          totalExpenses: 0,
          netProfit: 0,
          grossProfit: 0,
          profitMargin: 0,
          cashFlow: {
            operating: 0,
            investing: 0,
            financing: 0,
            net: 0
          },
          revenueBySource: [],
          expenseBreakdown: [],
          monthlyTrend: [],
          financialRatios: {
            currentRatio: 0,
            quickRatio: 0,
            debtToEquity: 0,
            returnOnInvestment: 0
          }
        });
      }
    } catch (error) {
      console.error('Error fetching financial report:', error);
      toast.error('Failed to fetch financial report data');
      // Set empty state on error
      setFinancialData({
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        grossProfit: 0,
        profitMargin: 0,
        cashFlow: {
          operating: 0,
          investing: 0,
          financing: 0,
          net: 0
        },
        revenueBySource: [],
        expenseBreakdown: [],
        monthlyTrend: [],
        financialRatios: {
          currentRatio: 0,
          quickRatio: 0,
          debtToEquity: 0,
          returnOnInvestment: 0
        }
      });
    } finally {
      setLoading(false);
    }
  }, [dateRange]);


  // Load data on component mount and date change
  useEffect(() => {
    fetchFinancialReport();
  }, [fetchFinancialReport]);

  // Export report
  const handleExportReport = async (format) => {
    try {
      const response = await apiService.exportFinancialReport(dateRange.startDate, dateRange.endDate, format);
      if (response.success) {
        // Create download link
        const blob = new Blob([response.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `financial-report-${dateRange.startDate}-to-${dateRange.endDate}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Financial report exported successfully as ${format.toUpperCase()}!`, {
          duration: 3000,
          icon: '💰',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export financial report');
    }
  };

  // Handle Profit and Loss report
  const handleProfitAndLoss = () => {
    setShowProfitLossModal(true);
  };

  // Handle Income Statement report
  const handleIncomeStatement = () => {
    setShowIncomeStatementModal(true);
  };

  // Close modals
  const closeProfitLossModal = () => {
    setShowProfitLossModal(false);
  };

  const closeIncomeStatementModal = () => {
    setShowIncomeStatementModal(false);
  };

  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8 lg:mb-10">
              <div className="flex flex-col gap-4 sm:gap-6">
                {/* Title and Date Row */}
                <div className="flex flex-col sm:flex-row mt-16 sm:items-center sm:justify-between gap-4">
                  <div className="space-y-2">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                      Financial Report
                    </h1>
                    {/* <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Comprehensive financial analytics and performance insights</p> */}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <button
                      onClick={fetchFinancialReport}
                      disabled={loading}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {loading ? 'Loading...' : 'Apply Filter'}
                    </button>
                    <button
                      onClick={() => {
                        console.log('Manual refresh triggered');
                        fetchFinancialReport();
                      }}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh Data
                    </button>
                  </div>
                </div>
                
                {/* Export Buttons Row */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => handleExportReport('pdf')}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </button>
                  <button
                    onClick={() => handleExportReport('excel')}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                  </button>
                  <button
                    onClick={handleProfitAndLoss}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <BarChart className="w-4 h-4 mr-2" />
                    Profit & Loss
                  </button>
                  <button
                    onClick={handleIncomeStatement}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Income Statement
                  </button>
                </div>
              </div>
            </div>


            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Total Revenue */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{formatCurrency(financialData.totalRevenue)}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Total Expenses */}
              <div className="bg-gradient-to-br from-white via-red-50/30 to-rose-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-red-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                    <p className="text-2xl sm:text-3xl font-bold text-red-600">{formatCurrency(financialData.totalExpenses)}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-rose-600 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Net Profit */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Net Profit</p>
                    <p className={`text-2xl sm:text-3xl font-bold ${financialData.netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {formatCurrency(financialData.netProfit)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${financialData.netProfit >= 0 ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Profit Margin */}
              <div className="bg-gradient-to-br from-white via-purple-50/30 to-violet-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-purple-100/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Profit Margin</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-600">{formatPercentage(financialData.profitMargin)}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Cash Flow Analysis */}
            <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm mb-6 sm:mb-8">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                  <Activity className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Cash Flow Analysis</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Operating</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(financialData.cashFlow.operating)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Investing</p>
                  <p className="text-xl font-bold text-purple-600">{formatCurrency(financialData.cashFlow.investing)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Financing</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(financialData.cashFlow.financing)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Net Cash Flow</p>
                  <p className={`text-xl font-bold ${financialData.cashFlow.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(financialData.cashFlow.net)}
                  </p>
                </div>
              </div>
            </div>

            {/* Charts and Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Revenue by Source */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                    <PieChart className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Revenue by Source</h3>
                </div>
                <div className="space-y-3">
                  {financialData.revenueBySource.map((source, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-green-500' : index === 1 ? 'bg-blue-500' : index === 2 ? 'bg-purple-500' : 'bg-orange-500'}`}></div>
                        <span className="text-sm font-medium text-gray-700">{source.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(source.amount)}</p>
                        <p className="text-xs text-gray-500">{source.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expense Breakdown */}
              <div className="bg-gradient-to-br from-white via-red-50/30 to-rose-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-red-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-rose-600 rounded-lg flex items-center justify-center mr-3">
                    <Receipt className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Expense Breakdown</h3>
                </div>
                <div className="space-y-3">
                  {financialData.expenseBreakdown.map((expense, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : index === 2 ? 'bg-yellow-500' : 'bg-pink-500'}`}></div>
                        <span className="text-sm font-medium text-gray-700">{expense.category}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(expense.amount)}</p>
                        <p className="text-xs text-gray-500">{expense.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Financial Ratios */}
            <div className="bg-gradient-to-br from-white via-purple-50/30 to-violet-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-purple-100/50 backdrop-blur-sm mb-6 sm:mb-8">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center mr-3">
                  <BarChart3 className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Financial Ratios</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Current Ratio</p>
                  <p className="text-xl font-bold text-purple-600">{financialData.financialRatios.currentRatio.toFixed(2)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Quick Ratio</p>
                  <p className="text-xl font-bold text-blue-600">{financialData.financialRatios.quickRatio.toFixed(2)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">Debt to Equity</p>
                  <p className="text-xl font-bold text-orange-600">{financialData.financialRatios.debtToEquity.toFixed(2)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-gray-600">ROI</p>
                  <p className="text-xl font-bold text-green-600">{formatPercentage(financialData.financialRatios.returnOnInvestment)}</p>
                </div>
              </div>
            </div>

            {/* Monthly Trend Chart */}
            <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 sm:p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                  <LineChart className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Monthly Financial Trend</h3>
              </div>
              <div className="h-64 flex items-end justify-between space-x-2 p-4 bg-white/50 rounded-lg">
                {financialData.monthlyTrend.map((month, index) => (
                  <div key={index} className="flex flex-col items-center space-y-2">
                    <div 
                      className="w-8 bg-gradient-to-t from-blue-500 to-indigo-600 rounded-t-lg transition-all duration-300 hover:from-blue-600 hover:to-indigo-700"
                      style={{ height: `${(month.revenue / Math.max(...financialData.monthlyTrend.map(m => m.revenue))) * 200}px` }}
                    ></div>
                    <div className="text-xs text-gray-600 text-center">
                      <p className="font-medium">{month.month}</p>
                      <p className="text-gray-500">{formatCurrency(month.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

      </div>

      {/* Profit and Loss Modal */}
      {showProfitLossModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <BarChart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Profit & Loss Statement</h2>
                  <p className="text-purple-100 text-sm">Period: {dateRange.startDate} to {dateRange.endDate}</p>
                </div>
              </div>
              <button
                onClick={closeProfitLossModal}
                className="p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Revenue Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Revenue
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-green-200">
                      <span className="text-green-700 font-medium">Sales Revenue</span>
                      <span className="text-green-800 font-bold">{formatCurrency(financialData.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-green-200">
                      <span className="text-green-700 font-medium">Other Income</span>
                      <span className="text-green-800 font-bold">{formatCurrency(0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-green-100 rounded-lg px-3">
                      <span className="text-green-800 font-bold text-lg">Total Revenue</span>
                      <span className="text-green-900 font-bold text-lg">{formatCurrency(financialData.totalRevenue)}</span>
                    </div>
                  </div>
                </div>

                {/* Expenses Section */}
                <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-lg p-4 border border-red-200">
                  <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center">
                    <TrendingDown className="w-5 h-5 mr-2" />
                    Expenses
                  </h3>
                  <div className="space-y-3">
                    {/* Dynamic expense categories from financial data */}
                    {financialData.expenseBreakdown && financialData.expenseBreakdown.length > 0 ? (
                      financialData.expenseBreakdown.map((expense, index) => (
                        <div key={`expense-${index}`} className="flex justify-between items-center py-2 border-b border-red-200">
                          <span className="text-red-700 font-medium">{expense.category}</span>
                          <span className="text-red-800 font-bold">{formatCurrency(expense.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-red-600">
                        <p>No expense data available for the selected period</p>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center py-3 bg-red-100 rounded-lg px-3">
                      <span className="text-red-800 font-bold text-lg">Total Expenses</span>
                      <span className="text-red-900 font-bold text-lg">{formatCurrency(financialData.totalExpenses)}</span>
                    </div>
                  </div>
                </div>

                {/* Net Profit Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex justify-between items-center py-3">
                    <span className="text-blue-800 font-bold text-xl">Net Profit/Loss</span>
                    <span className={`font-bold text-xl ${financialData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(financialData.netProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-blue-700 font-medium">Profit Margin</span>
                    <span className="text-blue-800 font-bold">{formatPercentage(financialData.profitMargin)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={closeProfitLossModal}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  toast.success('Profit & Loss report exported successfully!', {
                    duration: 3000,
                    icon: '📊',
                    style: {
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                    },
                  });
                  closeProfitLossModal();
                }}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
              >
                <Download className="w-4 h-4 mr-2 inline" />
                Export PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Income Statement Modal */}
      {showIncomeStatementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Income Statement</h2>
                  <p className="text-indigo-100 text-sm">Period: {dateRange.startDate} to {dateRange.endDate}</p>
                </div>
              </div>
              <button
                onClick={closeIncomeStatementModal}
                className="p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Operating Revenue */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Operating Revenue
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-green-200">
                      <span className="text-green-700 font-medium">Gross Sales</span>
                      <span className="text-green-800 font-bold">{formatCurrency(financialData.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-green-200">
                      <span className="text-green-700 font-medium">Less: Returns & Allowances</span>
                      <span className="text-green-800 font-bold">{formatCurrency(0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-green-100 rounded-lg px-3">
                      <span className="text-green-800 font-bold text-lg">Net Sales</span>
                      <span className="text-green-900 font-bold text-lg">{formatCurrency(financialData.totalRevenue)}</span>
                    </div>
                  </div>
                </div>

                {/* Cost of Goods Sold */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
                  <h3 className="text-lg font-bold text-orange-800 mb-4 flex items-center">
                    <Package className="w-5 h-5 mr-2" />
                    Cost of Goods Sold
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-orange-200">
                      <span className="text-orange-700 font-medium">Beginning Inventory</span>
                      <span className="text-orange-800 font-bold">{formatCurrency(0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-orange-200">
                      <span className="text-orange-700 font-medium">Purchases</span>
                      <span className="text-orange-800 font-bold">{formatCurrency(0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-orange-200">
                      <span className="text-orange-700 font-medium">Ending Inventory</span>
                      <span className="text-orange-800 font-bold">{formatCurrency(0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-orange-100 rounded-lg px-3">
                      <span className="text-orange-800 font-bold text-lg">Cost of Goods Sold</span>
                      <span className="text-orange-900 font-bold text-lg">{formatCurrency(0)}</span>
                    </div>
                  </div>
                </div>

                {/* Gross Profit */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex justify-between items-center py-3">
                    <span className="text-blue-800 font-bold text-xl">Gross Profit</span>
                    <span className="text-blue-900 font-bold text-xl">{formatCurrency(financialData.grossProfit)}</span>
                  </div>
                </div>

                {/* Operating Expenses */}
                <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-lg p-4 border border-red-200">
                  <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center">
                    <TrendingDown className="w-5 h-5 mr-2" />
                    Operating Expenses
                  </h3>
                  <div className="space-y-3">
                    {/* Dynamic expense categories from financial data */}
                    {financialData.expenseBreakdown && financialData.expenseBreakdown.length > 0 ? (
                      financialData.expenseBreakdown.map((expense, index) => (
                        <div key={`expense-${index}`} className="flex justify-between items-center py-2 border-b border-red-200">
                          <span className="text-red-700 font-medium">{expense.category}</span>
                          <span className="text-red-800 font-bold">{formatCurrency(expense.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-red-600">
                        <p>No expense data available for the selected period</p>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center py-3 bg-red-100 rounded-lg px-3">
                      <span className="text-red-800 font-bold text-lg">Total Operating Expenses</span>
                      <span className="text-red-900 font-bold text-lg">{formatCurrency(financialData.totalExpenses)}</span>
                    </div>
                  </div>
                </div>

                {/* Net Income */}
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex justify-between items-center py-3">
                    <span className="text-purple-800 font-bold text-xl">Net Income</span>
                    <span className={`font-bold text-xl ${financialData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(financialData.netProfit)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={closeIncomeStatementModal}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  toast.success('Income Statement report exported successfully!', {
                    duration: 3000,
                    icon: '📈',
                    style: {
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                    },
                  });
                  closeIncomeStatementModal();
                }}
                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
              >
                <Download className="w-4 h-4 mr-2 inline" />
                Export PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FinancialReport;
